"use client";

import { useEffect, useState } from "react";
type Pt = {
  t: number;
  flux: number;
};
type Channels = {
  long: Pt[];
  short: Pt[];
  sat: number | null;
};
type WindowKey = "6-hour" | "1-day" | "3-day";
const GREEN = "#4ade80";
const WINDOWS: {
  key: WindowKey;
  label: string;
}[] = [{
  key: "6-hour",
  label: "6H"
}, {
  key: "1-day",
  label: "1D"
}, {
  key: "3-day",
  label: "3D"
}];

// classify GOES-R true flux directly (no legacy 0.7 scaling — dropped by NOAA in 2020)
function classify(flux: number | null): {
  cls: string;
  color: string;
} {
  if (flux == null || !Number.isFinite(flux) || flux <= 0) return {
    cls: "—",
    color: "var(--muted-foreground)"
  };
  if (flux >= 1e-4) return {
    cls: `X${(flux / 1e-4).toFixed(1)}`,
    color: "var(--destructive)"
  }; // X → red
  if (flux >= 1e-5) return {
    cls: `M${(flux / 1e-5).toFixed(1)}`,
    color: "var(--flare-yellow)"
  }; // M → yellow
  if (flux >= 1e-6) return {
    cls: `C${(flux / 1e-6).toFixed(1)}`,
    color: "var(--photon-blue)"
  }; // C → blue
  if (flux >= 1e-7) return {
    cls: `B${(flux / 1e-7).toFixed(1)}`,
    color: GREEN
  };
  if (flux >= 1e-8) return {
    cls: `A${(flux / 1e-8).toFixed(1)}`,
    color: GREEN
  };
  return {
    cls: "<A",
    color: GREEN
  };
}
const SUP: Record<string, string> = {
  "-3": "⁻³",
  "-4": "⁻⁴",
  "-5": "⁻⁵",
  "-6": "⁻⁶",
  "-7": "⁻⁷",
  "-8": "⁻⁸",
  "-9": "⁻⁹"
};
const LOG_MIN = -9;
const LOG_MAX = -3;
const PL = 44,
  PR = 720,
  PT = 16,
  PB = 286;
const PW = PR - PL,
  PH = PB - PT;
function yForLog(l: number) {
  const c = Math.max(LOG_MIN, Math.min(LOG_MAX, l));
  return PT + (LOG_MAX - c) / (LOG_MAX - LOG_MIN) * PH;
}
function yForFlux(f: number) {
  return yForLog(Math.log10(f));
}
function buildPath(pts: Pt[], tMin: number, tMax: number): string {
  const span = tMax - tMin || 1;
  const xFor = (t: number) => PL + (t - tMin) / span * PW;
  let d = "";
  let prev = -Infinity;
  for (const p of pts) {
    if (!Number.isFinite(p.flux) || p.flux <= 0) {
      prev = -Infinity;
      continue;
    }
    const x = xFor(p.t);
    const y = yForFlux(p.flux);
    d += p.t - prev > 6 * 60 * 1000 ? `M ${x.toFixed(1)} ${y.toFixed(1)}` : ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
    prev = p.t;
  }
  return d;
}
function fmtTick(t: number, win: WindowKey): string {
  const d = new Date(t);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  if (win === "3-day") return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
  return `${hh}:${mm}`;
}
export default function GoesXrayFlux() {
  const [win, setWin] = useState<WindowKey>("1-day");
  const [data, setData] = useState<Channels | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  useEffect(() => {
    let alive = true;
    setStatus("loading");
    async function load() {
      try {
        const res = await fetch(`https://services.swpc.noaa.gov/json/goes/primary/xrays-${win}.json`, {
          cache: "no-store"
        });
        if (!res.ok) throw new Error("http");
        const raw: any[] = await res.json();
        if (!Array.isArray(raw) || raw.length === 0) throw new Error("empty");
        const long: Pt[] = [];
        const short: Pt[] = [];
        let sat: number | null = null;
        for (const r of raw) {
          const t = new Date(r.time_tag).getTime();
          const flux = Number(r.flux);
          sat = r.satellite ?? sat;
          if (r.energy === "0.1-0.8nm") long.push({
            t,
            flux
          });else if (r.energy === "0.05-0.4nm") short.push({
            t,
            flux
          });
        }
        long.sort((a, b) => a.t - b.t);
        short.sort((a, b) => a.t - b.t);
        if (!alive) return;
        setData({
          long,
          short,
          sat
        });
        setStatus("ok");
      } catch {
        if (alive) setStatus("error");
      }
    }
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [win]);
  const long = data?.long ?? [];
  const valid = long.filter(p => Number.isFinite(p.flux) && p.flux > 0);
  const tMin = long.length ? long[0].t : 0;
  const tMax = long.length ? long[long.length - 1].t : 1;
  const current = valid.length ? valid[valid.length - 1].flux : null;
  const peak = valid.reduce<Pt | null>((m, p) => p.flux > (m?.flux ?? -1) ? p : m, null);
  const background = valid.length ? Math.min(...valid.map(p => p.flux)) : null;
  const curClass = classify(current);
  const peakClass = classify(peak?.flux ?? null);
  const bgClass = classify(background);

  // trend over last ~20 min
  let trend = "—";
  if (valid.length > 20) {
    const a = valid[valid.length - 21].flux;
    const b = valid[valid.length - 1].flux;
    trend = b > a * 1.3 ? "▲ RISING" : b < a * 0.77 ? "▼ FALLING" : "● STEADY";
  }
  const ticks = Array.from({
    length: 5
  }, (_, i) => tMin + (tMax - tMin) * i / 4);
  const decades = [-8, -7, -6, -5, -4];
  const classBands = [{
    letter: "X",
    mid: -3.5
  }, {
    letter: "M",
    mid: -4.5
  }, {
    letter: "C",
    mid: -5.5
  }, {
    letter: "B",
    mid: -6.5
  }, {
    letter: "A",
    mid: -7.5
  }];
  return <section data-section-id="goes-xray" id="goes-xray" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-border relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[var(--corona-orange)]/5 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-px bg-[var(--corona-orange)]" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--corona-orange)]">
                LIVE FROM SWPC · GOES X-RAY FLUX
              </span>
            </div>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-foreground">
              Flare Watch
            </h2>
          </div>
          <div className="flex gap-1.5">
            {WINDOWS.map(w => <button key={w.key} onClick={() => setWin(w.key)} className={`font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded border transition-colors ${win === w.key ? "bg-foreground text-background border-foreground" : "bg-transparent text-foreground/40 border-secondary/20 hover:border-foreground/40"}`}>
                {w.label}
              </button>)}
          </div>
        </div>

        {/* Stat strip */}
        {status === "ok" && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-12">
            <div className="flex flex-col gap-3 border border-secondary/20 rounded-md p-5 md:p-6" style={{
          backgroundColor: "rgba(15, 17, 21, 0.6)",
          backdropFilter: "blur(24px)"
        }}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">CURRENT</span>
                <span className="size-1.5 animate-pulse" style={{
              backgroundColor: curClass.color
            }} />
              </div>
              <span className="font-default font-bold text-3xl md:text-4xl tabular-nums leading-none" style={{
            color: curClass.color
          }}>{curClass.cls}</span>
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
                <span style={{
              color: curClass.color
            }}>{trend}</span>
                <span className="text-foreground/40">{current ? current.toExponential(1) : "—"}</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 border border-secondary/20 rounded-md p-5 md:p-6" style={{
          backgroundColor: "rgba(15, 17, 21, 0.6)",
          backdropFilter: "blur(24px)"
        }}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">WINDOW PEAK</span>
                <span className="size-1.5 animate-pulse" style={{
              backgroundColor: peakClass.color
            }} />
              </div>
              <span className="font-default font-bold text-3xl md:text-4xl tabular-nums leading-none" style={{
            color: peakClass.color
          }}>{peakClass.cls}</span>
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
                <span style={{
              color: peakClass.color
            }}>{peak ? fmtTick(peak.t, win === "3-day" ? "1-day" : win) : "—"}</span>
                <span className="text-foreground/40">UTC</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 border border-secondary/20 rounded-md p-5 md:p-6" style={{
          backgroundColor: "rgba(15, 17, 21, 0.6)",
          backdropFilter: "blur(24px)"
        }}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">BACKGROUND</span>
                <span className="size-1.5 animate-pulse" style={{
              backgroundColor: GREEN
            }} />
              </div>
              <span className="font-default font-bold text-3xl md:text-4xl tabular-nums leading-none text-foreground">{bgClass.cls}</span>
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
                <span style={{
              color: GREEN
            }}>BASELINE</span>
                <span className="text-foreground/40">QUIET SUN</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 border border-secondary/20 rounded-md p-5 md:p-6" style={{
          backgroundColor: "rgba(15, 17, 21, 0.6)",
          backdropFilter: "blur(24px)"
        }}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/40">SOURCE</span>
                <span className="size-1.5 animate-pulse" style={{
              backgroundColor: GREEN
            }} />
              </div>
              <span className="font-default font-bold text-2xl md:text-3xl tabular-nums leading-none text-foreground">GOES-{data?.sat ?? "?"}</span>
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]">
                <span style={{
              color: GREEN
            }}>PRIMARY</span>
                <span className="text-foreground/40">1-8 Å</span>
              </div>
            </div>
          </div>}

        {/* Chart */}
        <div className="border border-secondary/20 rounded-md p-5 md:p-7" style={{
        backgroundColor: "rgba(15, 17, 21, 0.6)",
        backdropFilter: "blur(24px)"
      }}>
          {status === "loading" && <p className="font-mono text-xs uppercase tracking-widest text-foreground/40 animate-pulse py-16 text-center">Acquiring GOES telemetry…</p>}
          {status === "error" && <p className="font-mono text-xs uppercase tracking-widest text-destructive py-16 text-center">GOES feed unreachable. Retrying on next cycle.</p>}
          {status === "ok" && <>
              <svg viewBox="0 0 760 320" className="w-full block" role="img" aria-label="GOES X-ray flux over time, long and short wavelength channels, log scale A to X flare classes">
                {/* severity bands */}
                <rect x={PL} y={yForLog(-3)} width={PW} height={yForLog(-4) - yForLog(-3)} fill="var(--destructive)" opacity={0.06} />
                <rect x={PL} y={yForLog(-4)} width={PW} height={yForLog(-5) - yForLog(-4)} fill="var(--flare-yellow)" opacity={0.06} />

                {/* decade gridlines + flux labels */}
                {decades.map(d => <g key={d}>
                    <line x1={PL} y1={yForLog(d)} x2={PR} y2={yForLog(d)} stroke="var(--border)" strokeOpacity={0.25} strokeWidth={1} />
                    <text x={PL - 6} y={yForLog(d) + 3} textAnchor="end" className="font-mono" fontSize={9} fill="var(--muted-foreground)">10{SUP[String(d)]}</text>
                  </g>)}
                {/* class letters */}
                {classBands.map(b => <text key={b.letter} x={PR + 6} y={yForLog(b.mid) + 4} textAnchor="start" className="font-mono" fontSize={12} fill={b.letter === "X" ? "var(--destructive)" : b.letter === "M" ? "var(--flare-yellow)" : b.letter === "C" ? "var(--photon-blue)" : "var(--muted-foreground)"}>
    {b.letter}
  </text>)}
                {/* x ticks */}
                {ticks.map((t, i) => {
              const x = PL + (t - tMin) / (tMax - tMin || 1) * PW;
              return <g key={i}>
                      <line x1={x} y1={PT} x2={x} y2={PB} stroke="var(--border)" strokeOpacity={0.12} strokeWidth={1} />
                      <text x={x} y={PB + 16} textAnchor="middle" className="font-mono" fontSize={9} fill="var(--muted-foreground)">{fmtTick(t, win)}</text>
                    </g>;
            })}

                {/* short channel (secondary, dim) */}
                <path d={buildPath(data!.short, tMin, tMax)} fill="none" stroke="var(--muted-foreground)" strokeWidth={1} strokeOpacity={0.45} />
                {/* long channel (primary) */}
                <path d={buildPath(long, tMin, tMax)} fill="none" stroke="var(--corona-orange)" strokeWidth={1.6} />

                {/* current marker */}
                {current && <>
                    <line x1={PL} y1={yForFlux(current)} x2={PR} y2={yForFlux(current)} stroke={curClass.color} strokeWidth={1} strokeDasharray="3 4" strokeOpacity={0.5} />
                    <circle cx={PR} cy={yForFlux(current)} r={3.5} fill={curClass.color} />
                  </>}
              </svg>

              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-4 h-0.5" style={{
                background: "var(--corona-orange)"
              }} />LONG 1-8 Å · FLARE CLASS</span>
                <span className="flex items-center gap-1.5"><span className="w-4 h-0.5" style={{
                background: "var(--muted-foreground)"
              }} />SHORT 0.5-4 Å</span>
                <span className="text-foreground/40">SOURCE NOAA SWPC · GOES-{data?.sat ?? "?"} · UTC</span>
              </div>
            </>}
        </div>

        {/* Beginner blurb */}
        <div className="mt-10 border-l-2 border-[var(--corona-orange)]/60 bg-card/20 rounded-r-md pl-5 pr-5 py-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--corona-orange)] mb-3">READING THE FLUX</p>
          <ul className="space-y-2.5 font-serif text-[14px] leading-relaxed text-muted-foreground">
            <li><span className="text-foreground" data-ast-id="f:/src/pages/CitizenScience/sections/Telemetryvisualization.tsx|sig:2owfgy" data-ast-start="15337" data-ast-end="15431">The bold line is the Sun's X-ray brightness right now</span> — measured by a satellite, updated every minute. It's the long-wavelength (1 - 8 Å) channel, the one that defines a flare's class.</li>
            <li><span className="text-foreground">A flare is a spike.</span> Its class is the peak height on the A → X scale, and each letter up is ten times more energy: C is minor, M is moderate (can disrupt HF radio), X is major.</li>
            <li><span className="text-foreground" data-ast-id="f:/src/pages/CitizenScience/sections/Telemetryvisualization.tsx|sig:2owfgy" data-ast-start="15621" data-ast-end="15673">A quiet Sun</span> sits low and flat in the A–B range. The baseline creeping up is often the first sign a region is waking up.</li>
            <li><span className="text-foreground" data-ast-id="f:/src/pages/CitizenScience/sections/Telemetryvisualization.tsx|sig:2owfgy" data-ast-start="16137" data-ast-end="16324">For imaging —</span> an active Sun means live chromosphere structure: flares, surging prominences, and brightening - worth catching in Hα while it's happening.</li>
          </ul>
        </div>
      </div>
    </section>;
}